import { GetWorkflowRequestsContract } from 'schemas/GetWorkflowRequests';
import { QueryClient } from 'react-query';
import { WorkflowRequestsCacheKeys, WorkflowRequestsStatusMap } from '../constants';
import { format } from 'date-fns';
import { ComponentAssignment, EditPayrollListItem } from './types';
import { WorkflowRequestDetailsQueryKey } from '../components/DetailsView/constants';

export const transformEditPayrollRequestData = (
  data: GetWorkflowRequestsContract['data'][0],
  peopleMetadata: GetWorkflowRequestsContract['people_metadata'],
): EditPayrollListItem => {
  const forPeopleId = data['for_people_id'] as number;
  const oldData = data.old_data as any;
  const newData = data.new_data as any;
  const payload = data.payload as any;

  const forPersonData = peopleMetadata[forPeopleId];
  const componentAssignments = (newData.componentAssignments ??
    null) as EditPayrollListItem['componentAssignments'];
  return {
    id: data.id,
    employeeDetails: {
      name: forPersonData.name,
      employeeId: `${forPersonData.id_prefix ?? ''}${forPersonData.employee_id}`,
    },
    payrollMonth: format(new Date(payload.payroll_month as string), 'MMMM'),
    approvalLevel: data.pending_on_level as number,
    status: data.status,
    deductions: {
      new: newData.deductions,
      oldSum: oldData?.sum_of_deductions,
      newSum: newData.sum_of_deductions || sumComponentAssignmentsInRupees(componentAssignments?.deduction),
    },
    arrears: {
      old: oldData?.sum_of_arrears,
      new: newData.sum_of_arrears,
    },
    additions: {
      new: newData.additions,
      oldSum: oldData?.sum_of_allowances,
      newSum:
        newData.sum_of_allowances ||
        sumComponentAssignmentsInRupees(
          componentAssignments?.addition,
          componentAssignments?.variablePay,
        ),
    },
    overtime: {
      entries: (newData.overtimeEntries?.entries ?? []) as EditPayrollListItem['overtime']['entries'],
    },
    componentAssignments,
    allowAction: data.status === WorkflowRequestsStatusMap.pending,
    isRearchRequest: newData?.is_rearch_native ?? false,
    forPeopleId: (data.for_people_id as number | null) ?? null,
    overtimePayrollMonth: (newData.overtimeEntries?.payrollMonth as string | undefined) ?? null,
  };
};

const sumComponentAssignmentsInRupees = (
  ...assignmentLists: (ComponentAssignment[] | null | undefined)[]
): number => {
  const paise = assignmentLists
    .flatMap((list) => list ?? [])
    .reduce((sum, assignment) => sum + Number(assignment?.value ?? 0), 0);
  return paise / 100;
};

export const hasComponentAssignmentsData = (
  componentAssignments: EditPayrollListItem['componentAssignments'],
): boolean => {
  if (!componentAssignments) {
    return false;
  }
  return Boolean(
    componentAssignments.addition?.length ||
      componentAssignments.variablePay?.length ||
      componentAssignments.deduction?.length ||
      componentAssignments.lop !== null,
  );
};

export const invalidateEditPayrollCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries(WorkflowRequestsCacheKeys.editPayroll.pending);
  queryClient.invalidateQueries(WorkflowRequestsCacheKeys.editPayroll.initiatedByMe);
  queryClient.invalidateQueries(WorkflowRequestsCacheKeys.editPayroll.completed);

  // invalidating workflow-summary cache
  queryClient.invalidateQueries(WorkflowRequestsCacheKeys.summaryForUser);

  // invalidating workflow-request-details cache
  queryClient.invalidateQueries(WorkflowRequestDetailsQueryKey);
};
